import { makeStyles } from "@mui/styles";
import { colors } from "../../styles/colors";
import { down, hoverOnly } from "../../styles/breakpoints";
import { pageShellRoot, pageContentRootWide, fullBleedOnMobile } from "../../styles/pageShell";

const useStyles = makeStyles((_) => ({
  /* The sidebar shell sizes and scrolls this; the old `minHeight: 100vh` plus
     `overflowY: auto` made it a second, competing scroll container. */
  contentMain: pageShellRoot,

  /*
   * `width: calc(100% - 64px)` alongside `padding: 32px` double-counted the
   * gutter — `box-sizing: border-box` already puts the padding inside the
   * width — so 64px went missing and `margin: 0 auto` split the remainder
   * into uneven side margins.
   */
  /* No width here on purpose: this class lands on <main>, which carries an
     inline margin-left for the sidebar. A declared width resolves against the
     full viewport and the margin is added on top, pushing the page's right
     edge into the shell's `overflow: hidden` where it is clipped away. */
  brandsRoot: pageContentRootWide,

  contentWrapper: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    position: "relative",
  },

  containerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "24px",
    gap: "16px",
    /* Was 960px, which is neither the sidebar's 900px drawer breakpoint nor
       any other value in the app. */
    [down.md]: {
      flexDirection: "column",
      alignItems: "stretch",
      marginBottom: "20px",
    },
    [down.sm]: {
      marginBottom: "16px",
      gap: "12px",
    },
  },

  divIconArrow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flex: "1 1 auto",
    minWidth: 0,
    "& h2": {
      color: colors.BLACK,
      /* Replaces a flat 28px, which wrapped to three lines on a phone. */
      fontSize: "clamp(20px, 2.2vw + 12px, 28px)",
      fontWeight: 700,
      margin: 0,
      letterSpacing: "0.5px",
      minWidth: 0,
      overflowWrap: "anywhere",
    },
  },

  iconCircle: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    backgroundColor: colors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(8, 152, 255, 0.3)",
    flexShrink: 0,
    [down.md]: {
      width: "48px",
      height: "48px",
      borderRadius: "10px",
    },
    [down.sm]: {
      width: "44px",
      height: "44px",
      borderRadius: "8px",
    },
  },

  addButton: {
    flexShrink: 0,
    whiteSpace: "nowrap",
    [down.md]: {
      width: "100%",
      "& button": { width: "100%", justifyContent: "center" },
    },
  },

  filterBar: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "20px",
    padding: "16px 20px",
    backgroundColor: colors.WHITE,
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    border: `1px solid ${colors.default}`,
    [down.sm]: {
      /* Stacked, so each control gets the full width instead of two cramped
         half-width fields. */
      flexDirection: "column",
      alignItems: "stretch",
      gap: "10px",
      padding: "12px",
      borderRadius: "8px",
    },
  },

  filterControl: {
    minWidth: "200px",
    flex: "1 1 200px",
    maxWidth: "300px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.WHITE,
      borderRadius: "8px",
    },
    [down.sm]: {
      minWidth: 0,
      width: "100%",
      maxWidth: "none",
    },
  },

  tableContainer: {
    backgroundColor: colors.WHITE,
    borderRadius: "12px",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
    border: `1px solid ${colors.default}`,
    minWidth: 0,
    [down.sm]: {
      /* Full-bleed, cancelling the page gutter so the table has the whole
         screen to scroll in. */
      ...fullBleedOnMobile,
    },
  },

  logoCell: {
    width: 44,
    height: 44,
    borderRadius: 8,
    objectFit: "contain",
    backgroundColor: colors.bgcolor,
    border: `1px solid ${colors.default}`,
  },

  rowActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  // ── model manager (inside the models dialog) ──
  modelRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 4px",
    minWidth: 0,
    borderBottom: `1px solid ${colors.default}`,
  },

  modelName: {
    flex: 1,
    minWidth: 0,
    fontSize: "14px",
    color: colors.BLACK,
    overflowWrap: "anywhere",
  },

  addModelRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "16px",
  },

  // ── logo dropzone in the add/edit modal ──
  imageUploadContainer: {
    position: "relative",
    width: "fit-content",
    maxWidth: "100%",
  },

  removeImageButton: {
    position: "absolute",
    top: "-8px",
    right: "-8px",
    backgroundColor: colors.WHITE,
    border: `1px solid ${colors.default}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
    [hoverOnly]: {
      "&:hover": { backgroundColor: colors.WHITE },
    },
  },
}));

export default useStyles;
